import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { VerwendungCardComponent } from '../../../components/verwendung-card/verwendung-card.component';
import { TestdatenblattUsage, TestdatenblattUsageRef } from '../../../models/testdatenblatt.model';
import { VerwendungEintrag, VerwendungReiter, VerwendungStatus } from '../../../models/verwendung.model';
import { TestdatenblattService } from '../../../services/testdatenblatt.service';

const KEY = 'testdatenblaetter.form.usage.';
const KEINE_VERWENDUNG: TestdatenblattUsage = { testModules: [], testCases: [] };

@Component({
  selector: 'app-testdatenblatt-usage',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, VerwendungCardComponent],
  template: `
    <app-verwendung-card
      testId="tdb-usage-card"
      [subtitle]="'testdatenblaetter.form.usage.subtitle' | translate"
      [tabs]="tabs()"
      [status]="status()"
      (erneutVersuchen)="erneutLaden()"
    />
  `,
})
export class TestdatenblattUsageComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(TestdatenblattService);
  private readonly translate = inject(TranslateService);

  readonly projectId = input.required<string>();
  readonly testdatenblattId = input<string | null>(null);

  protected readonly status = signal<VerwendungStatus>('bereit');
  private readonly usage = signal<TestdatenblattUsage>(KEINE_VERWENDUNG);
  private readonly sprachwechsel = toSignal(this.translate.onLangChange, { initialValue: null });
  private laden: Subscription | null = null;

  protected readonly tabs = computed<VerwendungReiter[]>(() => {
    this.sprachwechsel();
    const projectId = this.projectId();
    const { testModules, testCases } = this.usage();
    const text = (key: string): string => this.translate.instant(KEY + key);
    const eintrag = (ref: TestdatenblattUsageRef, bereich: string): VerwendungEintrag => ({
      id: ref.id,
      key: ref.key,
      titel: ref.title,
      ziel: { commands: ['/projects', projectId, bereich, ref.id, 'edit'] },
    });
    return [
      {
        id: 'tm',
        label: text('modules'),
        icon: 'content_copy',
        leerText: text('modulesEmpty'),
        suchPlatzhalter: text('modulesSearch'),
        eintraege: testModules.map((ref) => eintrag(ref, 'test-modules')),
      },
      {
        id: 'tc',
        label: text('testCases'),
        icon: 'folder',
        leerText: text('testCasesEmpty'),
        suchPlatzhalter: text('testCasesSearch'),
        eintraege: testCases.map((ref) => eintrag(ref, 'test-cases')),
      },
    ];
  });

  constructor() {
    effect(() => this.lade(this.projectId(), this.testdatenblattId()));
  }

  protected erneutLaden(): void {
    this.lade(this.projectId(), this.testdatenblattId());
  }

  private lade(projectId: string, testdatenblattId: string | null): void {
    this.laden?.unsubscribe();
    this.usage.set(KEINE_VERWENDUNG);
    if (!testdatenblattId) {
      this.status.set('bereit');
      return;
    }
    this.status.set('laedt');
    this.laden = this.service
      .getUsage(projectId, testdatenblattId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usage) => {
          this.usage.set(usage);
          this.status.set('bereit');
        },
        error: () => this.status.set('fehler'),
      });
  }
}
